import { useEffect, useRef, useState } from 'react';

/**
 * Two scroll events reversing direction within this window are the signature of the browser's
 * scroll offset and the collapse-driven layout shrink fighting each other, not a human changing
 * their mind — a deliberate reversal (scroll down, then back up to re-read something) takes far
 * longer than one animation frame.
 */
const REVERSAL_WINDOW_MS = 50;

/**
 * Mirrors `value` on every change — except once two changes in a row reverse direction within
 * `REVERSAL_WINDOW_MS`, which holds the last stable value until the burst goes quiet for
 * `quietMs`, then commits whatever `value` is by then. `quietMs <= 0` always commits instantly,
 * bypassing the guard.
 *
 * Built for `PageTitle`'s scroll-driven collapse: collapsing removes real in-flow height under a
 * `sticky top-0` header, so once collapseProgress crosses the threshold, the page's scrollable
 * height shrinks while the browser's own scroll offset (mid trackpad-momentum or a
 * `scroll-behavior: smooth` keyboard scroll) is still catching up to the same gesture. It clamps,
 * that clamp reads back as a reversal, and the header and the scroll offset chase each other for
 * the rest of the burst. A normal, monotonic scroll — the overwhelming majority of scrolling,
 * collapsing or expanding — never reverses direction, so it always commits instantly here; only
 * the chase itself gets held still until it resolves.
 */
export function useScrollProgressGuard(value: number, quietMs: number): number {
  const [stable, setStable] = useState(value);
  const prevValueRef = useRef(value);
  const prevDirectionRef = useRef(0);
  const prevTimeRef = useRef(now());
  const holdingRef = useRef(false);

  useEffect(() => {
    const currentTime = now();
    const direction = Math.sign(value - prevValueRef.current);
    const elapsedMs = currentTime - prevTimeRef.current;
    const isRapidReversal =
      quietMs > 0 &&
      direction !== 0 &&
      prevDirectionRef.current !== 0 &&
      direction !== prevDirectionRef.current &&
      elapsedMs < REVERSAL_WINDOW_MS;

    prevValueRef.current = value;
    prevTimeRef.current = currentTime;
    if (direction !== 0) prevDirectionRef.current = direction;
    if (isRapidReversal) holdingRef.current = true;

    if (!holdingRef.current || quietMs <= 0) {
      holdingRef.current = false;
      setStable(value);
      return undefined;
    }

    const timer = setTimeout(() => {
      holdingRef.current = false;
      setStable(value);
    }, quietMs);
    return () => clearTimeout(timer);
  }, [value, quietMs]);

  return stable;
}

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}
